/**
 * Shared modifier pipeline core.
 *
 * This module is the single synchronous implementation of modifier-stack
 * evaluation. Production callers reach it via the modifier worker; tests may
 * import it directly.
 */

import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import { dlog } from "../debug/debugLogger.ts";
import {
  composeProvenance,
  deriveFaceProvenance,
  remapFaceAttributes,
} from "../modeling/provenance.ts";
import type { FaceAttributeTable } from "../modeling/types.ts";
import { ensureWasmReady, isWasmReady } from "../wasm/init.ts";
import { subsurfCore } from "../wasm/subsurf.ts";
import { arrayCore } from "./modifiers/arrayCore.ts";
import { bevelCore } from "./modifiers/bevelCore.ts";
import { booleanCore } from "./modifiers/booleanCore.ts";
import { curveCore } from "./modifiers/curveCore.ts";
import { instanceCore } from "./modifiers/instanceCore.ts";
import { ensureManifoldReady } from "./modifiers/manifoldRuntime.ts";
import { mirrorCore } from "./modifiers/mirrorCore.ts";
import type {
  ModifierEvaluationInput,
  ModifierGeometry,
} from "./modifiers/modifierTypes.ts";
import { validateModifierGeometry } from "./modifiers/modifierTypes.ts";
import { noiseCore } from "./modifiers/noiseCore.ts";
import { shrinkwrapCore } from "./modifiers/shrinkwrapCore.ts";
import { skinCore } from "./modifiers/skinCore.ts";
import { smoothCore } from "./modifiers/smoothCore.ts";
import { solidifyCore } from "./modifiers/solidifyCore.ts";

if (process.env.NODE_ENV !== "test") {
  ensureWasmReady();
  ensureManifoldReady().catch(() => {});
}

function q(v: number): string {
  return (Math.round(v * 1000) / 1000).toFixed(3);
}

function modStepKey(
  mod: BlendModifier,
  input?: ModifierEvaluationInput,
): string {
  switch (mod.kind) {
    case "mirror":
      return `mirror|${mod.axisX}|${mod.axisY}|${mod.axisZ}|${mod.merge}|${mod.clipping}|${q(mod.tolerance)}|${mod.mirrorOrigin}`;
    case "subsurf":
      return `subsurf|${mod.subdivType}|${mod.levels}`;
    case "solidify":
      return `solidify|${q(mod.offset)}|${mod.offsetDirection}|${mod.vertexGroupId ?? ""}|${mod.vertexGroupIndices?.length ?? 0}`;
    case "array":
      return `array|${mod.count}|${mod.useRelativeOffset}|${mod.relativeOffset.map(q)}|${mod.useConstantOffset}|${mod.constantOffset.map(q)}|${mod.offsetMatrix?.map(q) ?? ""}`;
    case "skin":
      return `skin|${q(mod.depth)}|${mod.subdivisions}|${mod.fillCaps}|${mod.preserveVolume}|${mod.style ?? "smooth"}|${mod.strandCount ?? 3}|${q(mod.twistRate ?? 3)}|${q(mod.bundleJitter ?? 0.3)}|${q(mod.ribFrequency ?? 8)}|${q(mod.ribDepth ?? 0.5)}|${q(mod.linkLength ?? 0.04)}|${q(mod.linkAspect ?? 0.6)}|${q(mod.wireThickness ?? 0.005)}|${q(mod.linkOverlap ?? 0.4)}|${q(mod.twistPerLink ?? 90)}|${mod.linkSegments ?? 24}|${mod.vertexGroupId ?? ""}|${mod.vertexGroupIndices?.length ?? 0}`;
    case "instance":
      return `instance|${mod.sourceObjectName ?? ""}|${mod.placementMode ?? "origin"}|${mod.orientation ?? "local"}|${mod.instanceLimit ?? 64}|${mod.scatterMode ? 1 : 0}|${mod.instancesPerElement ?? 1}|${mod.jitterEnabled ? 1 : 0}|${q(mod.jitterScale ?? 0)}|${q(mod.jitterRotation ?? 0)}|${mod.showBase ? 1 : 0}|${input?.cacheKey ?? ""}`;
    case "shrinkwrap":
      return `shrinkwrap|${mod.targetObjectName ?? ""}|${mod.wrapMode}|${q(mod.offset)}|${mod.projectAxis}|${mod.projectPositive ? 1 : 0}|${mod.projectNegative ? 1 : 0}|${mod.vertexGroupId ?? ""}|${mod.vertexGroupIndices?.length ?? 0}|${input?.cacheKey ?? ""}`;
    case "curve":
      return `curve|${mod.targetObjectName ?? ""}|${mod.deformAxis}|${mod.useCurveRadius ? 1 : 0}|${input?.cacheKey ?? ""}`;
    case "noise": {
      const layerKeys = mod.layers
        .map((l) =>
          l.enabled
            ? `${l.noiseType}:${q(l.strength)}:${q(l.scale)}:${l.seed | 0}:${l.axisX ? 1 : 0}${l.axisY ? 1 : 0}${l.axisZ ? 1 : 0}:${l.vertexGroupId ?? ""}:${l.vertexGroupIndices?.length ?? 0}`
            : "off",
        )
        .join(",");
      return `noise|${mod.layers.length}|${layerKeys}`;
    }
    case "followObject":
      return `followObject|${mod.targetObjectName ?? ""}|${mod.targetBoneName ?? ""}|${mod.followPosition ? 1 : 0}|${mod.followRotation ? 1 : 0}|${mod.followScale ? 1 : 0}`;
    case "boolean":
      return `boolean|${mod.operation}|${mod.targetObjectName ?? ""}|${mod.solver}|${mod.selfIntersect ? 1 : 0}|${q(mod.holeTolerance)}|${input?.cacheKey ?? ""}`;
    case "smooth":
      return `smooth|${mod.iterations | 0}|${q(mod.strength)}|${mod.vertexGroupId ?? ""}|${mod.vertexGroupIndices?.length ?? 0}`;
    case "bevel":
      return `bevel|${mod.limitMethod}|${q(mod.angleLimit)}|${mod.vertexGroupId ?? ""}|${mod.vertexGroupIndices?.length ?? 0}|${mod.affect}|${q(mod.offset)}|${mod.segments | 0}|${q(mod.profile)}|${mod.clampOverlap ? 1 : 0}|${mod.markSharp ? 1 : 0}|${mod.markSeam ? 1 : 0}`;
  }
}

export function modifierStackKey(
  mods: BlendModifier[],
  input?: ModifierEvaluationInput,
): string {
  return mods
    .filter((m) => m.enabled)
    .map((mod) => modStepKey(mod, input))
    .join(">");
}

let _skinRadiiIdCounter = 0;
const _skinRadiiIds = new WeakMap<Float32Array, number>();

function skinRadiiId(radii: Float32Array): number {
  let id = _skinRadiiIds.get(radii);
  if (id === undefined) {
    id = ++_skinRadiiIdCounter;
    _skinRadiiIds.set(radii, id);
  }
  return id;
}

let stepCacheByPositions = new WeakMap<
  Float32Array,
  Map<string, ModifierGeometry>
>();
let stepCacheByBaseKey = new Map<string, Map<string, ModifierGeometry>>();

const MAX_STEP_CACHE_ENTRIES = 32;

function pruneMap(map: Map<string, ModifierGeometry>): void {
  if (map.size <= MAX_STEP_CACHE_ENTRIES) return;
  const oldest = map.keys().next().value!;
  map.delete(oldest);
}

export function clearModifierCache(): void {
  stepCacheByPositions = new WeakMap();
  stepCacheByBaseKey = new Map();
}

function applyCoreMod(
  geo: ModifierGeometry,
  mod: BlendModifier,
  input?: ModifierEvaluationInput,
): ModifierGeometry {
  dlog.trace(
    "js",
    "modifier.apply",
    `type:${mod.kind} verts:${geo.positions.length / 3}`,
    {
      type: mod.kind,
      vertexCount: geo.positions.length / 3,
    },
  );
  if (import.meta.env.DEV) {
    validateModifierGeometry(geo, `before ${mod.kind}`);
  }

  let result: ModifierGeometry;
  switch (mod.kind) {
    case "mirror":
      result = mirrorCore(geo, mod);
      break;
    case "subsurf":
      result = isWasmReady() ? subsurfCore(geo, mod) : geo;
      break;
    case "solidify":
      result = solidifyCore(geo, mod);
      break;
    case "array":
      result = arrayCore(geo, mod);
      break;
    case "skin":
      result = skinCore(geo, mod);
      break;
    case "instance":
      result = instanceCore(geo, mod, input);
      break;
    case "shrinkwrap":
      result = shrinkwrapCore(geo, mod, input);
      break;
    case "curve":
      result = curveCore(geo, mod, input);
      break;
    case "noise":
      result = noiseCore(geo, mod);
      break;
    case "followObject":
      result = geo;
      break;
    case "boolean":
      result = booleanCore(geo, mod, input);
      break;
    case "smooth":
      result = smoothCore(geo, mod);
      break;
    case "bevel":
      result = isWasmReady() ? bevelCore(geo, mod) : geo;
      break;
  }

  if (import.meta.env.DEV) {
    validateModifierGeometry(result, `after ${mod.kind}`);
  }

  if (result !== geo) {
    const faceAttributes = remapModifierFaceAttributes(geo, result);
    result = {
      ...result,
      provenance: composeProvenance(geo.provenance, result.provenance),
      faceAttributes,
      faceProvenance: undefined,
    };
  }
  return result;
}

function remapModifierFaceAttributes(
  input: ModifierGeometry,
  result: ModifierGeometry,
): FaceAttributeTable | undefined {
  if (!input.faceAttributes) return result.faceAttributes;
  if (!result.topology) return result.faceAttributes;

  const newFaceCount = result.topology.offsets.length - 1;
  if (newFaceCount <= 0) return undefined;

  const faceProvenance =
    result.faceProvenance ??
    (input.topology
      ? deriveFaceProvenance(input.topology, result.topology, result.provenance)
      : undefined);
  if (!faceProvenance) return result.faceAttributes;

  return remapFaceAttributes(
    input.faceAttributes,
    faceProvenance,
    newFaceCount,
  );
}

export function applyModifiersCore(
  geo: ModifierGeometry,
  modifiers: BlendModifier[],
  input?: ModifierEvaluationInput,
): ModifierGeometry {
  const enabledMods = modifiers.filter((m) => m.enabled);
  if (enabledMods.length === 0) return geo;

  let result = geo;
  for (const mod of enabledMods) {
    result = applyCoreMod(result, mod, input);
  }
  return result;
}

function getStepCache(
  geo: ModifierGeometry,
  baseCacheKey?: string,
): Map<string, ModifierGeometry> {
  if (baseCacheKey) {
    let cache = stepCacheByBaseKey.get(baseCacheKey);
    if (!cache) {
      cache = new Map();
      stepCacheByBaseKey.set(baseCacheKey, cache);
    }
    return cache;
  }

  let cache = stepCacheByPositions.get(geo.positions);
  if (!cache) {
    cache = new Map();
    stepCacheByPositions.set(geo.positions, cache);
  }
  return cache;
}

export function applyModifiers(
  geo: ModifierGeometry,
  modifiers: BlendModifier[],
  input?: ModifierEvaluationInput,
  baseCacheKey?: string,
): ModifierGeometry {
  const enabledMods = modifiers.filter((m) => m.enabled);
  if (enabledMods.length === 0) return geo;

  const cache = getStepCache(geo, baseCacheKey);
  const skinRadiiVer = geo.skinRadii ? skinRadiiId(geo.skinRadii) : undefined;
  const getStepKey = (mod: BlendModifier): string => {
    const base = modStepKey(mod, input);
    return mod.kind === "skin" && skinRadiiVer != null
      ? `${base}|r${skinRadiiVer}`
      : base;
  };

  let result: ModifierGeometry = geo;
  let cumulativeKey = "";

  for (const mod of enabledMods) {
    const stepKey = getStepKey(mod);
    cumulativeKey = cumulativeKey ? `${cumulativeKey}>${stepKey}` : stepKey;

    const cached = cache.get(cumulativeKey);
    if (cached) {
      result = cached;
      continue;
    }

    result = applyCoreMod(result, mod, input);
    cache.set(cumulativeKey, result);
    pruneMap(cache);
  }

  return result;
}
